import React from 'react';
import { useStateRef } from './use-state-ref';
import { getTouchEventData } from '../utils/get-touch-event';
import { getRefValue } from '../utils/get-ref-value';
const MIN_SWIPE_REQUIRED = 40;

export default function useSwiper(callback: any) {
  // States
  const [swipeMotion, setSwipeMotion] = React.useState(true);
  const [isSwiping, setIsSwiping] = React.useState(true);

  // Refs
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const currentOffsetXRef = React.useRef(0);
  const containerWidthRef = React.useRef(0);
  const startXRef = React.useRef(0);
  const minOffsetXRef = React.useRef(0);
  const [offsetX, setOffsetX, offsetXRef] = useStateRef(0);

  const onTouchMove = (e: TouchEvent | MouseEvent) => {
    setIsSwiping(false);

    const currentX = getTouchEventData(e).clientX;
    const diff = getRefValue(startXRef) - currentX;
    let newOffsetX = getRefValue(currentOffsetXRef) - diff;

    const maxOffsetX = 0;
    const minOffsetX = getRefValue(minOffsetXRef);

    if (newOffsetX > maxOffsetX) {
      newOffsetX = maxOffsetX;
    }

    if (newOffsetX < minOffsetX) {
      newOffsetX = minOffsetX;
    }

    setOffsetX(newOffsetX);
  };

  const onTouchEnd = () => {
    const currentOffsetX = getRefValue(currentOffsetXRef);
    const containerWidth = getRefValue(containerWidthRef);
    let newOffsetX = getRefValue(offsetXRef);

    const diff = currentOffsetX - newOffsetX;

    setSwipeMotion(false);

    // we need to check difference in absolute/positive value (if diff is more than 40px)
    if (Math.abs(diff) > MIN_SWIPE_REQUIRED) {
      if (diff > 0) {
        // swipe to the right if diff is positive
        newOffsetX = Math.floor(newOffsetX / containerWidth) * containerWidth;
      } else {
        // swipe to the left if diff is negative
        newOffsetX = Math.ceil(newOffsetX / containerWidth) * containerWidth;
      }
    } else {
      // remain in the current image
      newOffsetX = Math.round(newOffsetX / containerWidth) * containerWidth;
    }

    setOffsetX(newOffsetX);

    const currentIndexValue = Math.abs(newOffsetX / containerWidth);

    callback(currentIndexValue);

    window.removeEventListener('touchend', onTouchEnd);
    window.removeEventListener('touchmove', onTouchMove);
    window.removeEventListener('mouseup', onTouchEnd);
    window.removeEventListener('mousemove', onTouchMove);

    setIsSwiping(true);
  };

  const onTouchStart = (
    e: React.TouchEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>,
  ) => {
    currentOffsetXRef.current = getRefValue(offsetXRef);
    startXRef.current = getTouchEventData(e).clientX;

    setSwipeMotion(true);

    const containerEl = getRefValue(containerRef);
    const containerWidth = containerEl.offsetWidth;

    containerWidthRef.current = containerWidth;
    minOffsetXRef.current = containerWidth - containerEl.scrollWidth;

    window.addEventListener('touchmove', onTouchMove);
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('mousemove', onTouchMove);
    window.addEventListener('mouseup', onTouchEnd);
  };

  return {
    onTouchStart,
    offsetX,
    setOffsetX,
    swipeMotion,
    isSwiping,
    containerRef,
  };
}
